/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import Dropdown, {createOptions, noneOption} from '../components/menus/Dropdown';

/**
 * Defines the prop definitions for TimeFormatChooser,
 *
 * required :         Indicates field is required or not.
 * readOnly :         Indicates whether the dropdown is read-only mode.
 * value :            value from parent component to control this component from out.
 * defaultValue :     Default value to display when intially renders.
 * onChange :         The callback to be called when the value changes.
 * includeNone :      The flag indicates whether to add onone option into the drop down options.
 * includeNoneLabel : None option label
 */
export interface TimeFormatChooserProps {
  required?: boolean;
  readOnly?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: Function;
  includeNone?: boolean;
  includeNoneLabel?: string;
};

// the function returns the time format options...
export const getTimeFormatOptions = (includeNone = false, includeNoneLabel?: string) => {
  const timeFormatOptions = translate('common:timeFormat.options', {returnObjects: true});
  const options = createOptions({
    items     : timeFormatOptions,
    labelProp : 'label',
    valProp   : 'value'
  });
  return noneOption({
    label : includeNoneLabel,
    value : 'none',
    includeNone,
    options
  });
};

/**
 * Defines the TimeFormatChooser component. The TimeFormatChooser provides a list of time format
 * display within a dropdown list and allows the user to select a time format from the list
 * which will be returned to the  client as the selected time format.
 *
 * ```typescript
 *
 *  import TimeFormatChooser from '@ion/dls/src/editors/';
 *
 *  <TimeFormatChooser includeNone
 *    required
 *    systemFormatAsDefault
 *    includeNoneLabel={translate('common:editors.timeFormat.default')}
 *    onChange={onChange}
 *    value={state[name].value}
 *    defaultValue={state[name].value} />
 *
 * ```
 */
export const TimeFormatChooser = ({
  value,
  defaultValue,
  onChange,
  required = false,
  readOnly = false,
  includeNone = false,
  includeNoneLabel = translate('common:options.none')
}: TimeFormatChooserProps) => {
  const [format, setFormat] = useState(null);
  const options             = getTimeFormatOptions(includeNone, includeNoneLabel);

  useEffect(() => {
    !!value && setFormat(value);
  }, [value]);

  useEffect(() => {
    const defValue  = options.find(({value}) => defaultValue === value)?.value;
    if (!isUndefined(defValue)) {
      onChange?.(defValue);
      setFormat(defValue);
    }
  }, []);

  return (
    <Dropdown required={required}
      labelAsValue={false}
      displaySelectedLabel
      disabled={readOnly}
      value={format}
      options={options}
      placeholder={translate('common:timeFormat.placeholder')}
      fieldLabel={translate('common:timeFormat.label')}
      onChange={function({target: {value}}) {
        setFormat(value);
        onChange?.(value);
      }}/>
  );
};

export default TimeFormatChooser;
